import lodashGet from "lodash/get";
import lodashSet from "lodash/set";

import { getClientTimezone } from "@/utils/date";
import { appFeatures } from "@/config/services";

import { UserInfo, UserProfile } from "./UserService.types";
import TIMEZONE_LIST from "./temp/timezones.json";

export function getUserFullName(user: UserInfo) {
  return [user.given_name, user.family_name].filter(Boolean).join(" ");
}

export function getDefaultTimezone() {
  const clientTimezone = getClientTimezone();

  if (!clientTimezone) {
    return "";
  }

  const defaultDetails = TIMEZONE_LIST.find(({ utc }) =>
    utc.some((item) => item === clientTimezone.timeZone)
  );

  if (!defaultDetails) {
    return "";
  }

  return defaultDetails.value;
}

export function sanitizeProfileData(profileData: UserProfile) {
  profileData = JSON.parse(JSON.stringify(profileData)) as UserProfile;

  (["about_me", "timezone"] as (keyof UserProfile)[]).forEach((fieldName) => {
    if (!profileData[fieldName]) {
      (profileData as any)[fieldName] = "";
    }
  });

  (
    [
      "profession",
      "packages",
      "portfolios",
      "links",
      "skills",
      "specializations",
    ] as (keyof UserProfile)[]
  ).forEach((fieldName) => {
    if (!profileData[fieldName]) {
      (profileData as any)[fieldName] = [];
    }
  });

  profileData.portfolios = profileData.portfolios.filter(
    (item) => !!Object.keys(item).length
  );

  profileData.specializations = profileData.specializations.filter(
    (item) => !!Object.keys(item).length
  );

  profileData.links = profileData.links.filter(
    ({ name, url }) => !!name && !!url
  );

  [
    {
      key: "misc.canOnboard",
      defaultValue: false,
    },
    {
      key: "misc.seenNotifications",
      defaultValue: false,
    },
    {
      key: "misc.notificationCount",
      defaultValue: 0,
    },
    {
      key: "misc.featuresInterest",
      defaultValue: {},
    },
    {
      key: "misc.pushNotificationsDevices",
      defaultValue: [],
    },
    {
      key: "misc.featuresFeedback",
      defaultValue: {},
    },
  ].forEach(({ key, defaultValue }) => {
    const currentValue = lodashGet(profileData, key);
    if (currentValue === undefined) {
      lodashSet(profileData, key, defaultValue);
    }
  });

  if (appFeatures.isSupported("ACCOUNT.USE_CLIENT_TIMEZONE_AS_DEFAULT")) {
    if (!profileData.timezone) {
      profileData.timezone = getDefaultTimezone();
    }
  }

  if (appFeatures.isSupported("ACCOUNT.EDIT_LANGUAGES")) {
    if (!profileData.languages) {
      profileData.languages = [];
    }
  }

  return profileData;
}

export function getProfileCompletionPercentage({
  profileData,
}: {
  profileData: UserProfile;
}) {
  let fieldsWithValue = 0;

  (["about_me", "timezone"] as (keyof UserProfile)[]).forEach((fieldName) => {
    if (!!profileData[fieldName]) {
      fieldsWithValue += 1;
    }
  });

  (
    [
      "profession",
      "packages",
      "portfolios",
      "links",
      "skills",
      "specializations",
    ] as (keyof UserProfile)[]
  ).forEach((fieldName) => {
    if (!!(profileData[fieldName] as any[]).length) {
      fieldsWithValue += 1;
    }
  });

  let value = (fieldsWithValue * 100) / 8;
  value = Math.round(value);
  value = Math.min(100, Math.max(0, value));

  return value;
}
